import React, { useState, useEffect, useMemo } from 'react';
import {
  MessageSquare as MessageSquareIcon,
  CheckSquare as CheckSquareIcon,
  FileText as FileTextIcon,
  LayoutDashboard as LayoutDashboardIcon,
} from 'lucide-react';

import Sidebar from './Sidebar';
import ChatView from './ChatView';
import TasksView from './TasksView';
import NotepadView from './NotepadView';
import DesktopDashboardWrapper from './DesktopDashboardWrapper';
import CalendarView from './CalendarView';
import ProfileSettings from './ProfileSettings';
import Avatar from './Avatar';
import FloatingDM from './FloatingDM';
import Breadcrumbs from './Breadcrumbs';
import WatchListenView from './WatchListenView';
import RSSView from './RSSView';
import FilmTVTracker from './FilmTVTracker';
import MediaView from './MediaView';
import { useNavigateWithParams } from '../hooks/useNavigateWithParams';
import MediaPlayer from './MediaPlayer';
import GlobalFilterBar from './GlobalFilterBar';

function TabButton({ active, onClick, icon, label, hideLabel, disabled, ...rest }) {
  return (
    <button
      {...rest}
      onClick={onClick}
      disabled={disabled}
      className={`group relative flex items-center justify-center p-2 transition-all active:scale-95 rounded-lg ${
        disabled
          ? 'opacity-20 cursor-not-allowed'
          : active
            ? 'text-zinc-900 scale-110'
            : 'text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100/50'
      }`}
    >
      {React.cloneElement(icon, { strokeWidth: active ? 2.5 : 2 })}
      {label && !hideLabel && (
        <span className="absolute -bottom-8 bg-zinc-900 text-white text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[100] shadow-sm">
          {label}
        </span>
      )}
    </button>
  );
}

const MUTED_COLORS = [
  { id: 'default', hex: '#ffffff' },
  { id: 'red', hex: '#fecaca' },
  { id: 'orange', hex: '#fed7aa' },
  { id: 'yellow', hex: '#fef08a' },
  { id: 'green', hex: '#bbf7d0' },
  { id: 'blue', hex: '#bfdbfe' },
  { id: 'purple', hex: '#ddd6fe' },
  { id: 'pink', hex: '#fbcfe8' },
];

import Clock from './Clock';

const DesktopLayout = (props) => {
  
  const navigate = useNavigateWithParams();
  const {
    state,
    dispatch,
    user,
    username,
    bio,
    userAvatar,
    motivationalMessage,
    activeChannel,
    activeChannelId,
    isDM,
    activeColorHex,
    currentView,
    location,
    headerClocks,
  } = props;
  const homeTz = props.profile?.cities?.[0]?.timezone || 'UTC';
  const homeCity = props.profile?.cities?.[0]?.name || '';

  const {
    expandedIds,
    editingChannelId,
    editChannelName,
    editChannelColor,
    editChannelDescription,
    editChannelParticipants,
    editError,
    chatInputHtml,
    editingTaskId,
    editTaskText,
    editTaskDueDate,
    editTaskVenueName,
    editTaskAddress,
    editTaskAddressLat,
    editTaskAddressLng,
    deletingTaskId,
    isSavingNote,
    isSpellCheckEnabled,
    calendarDate,
    showOnlyStarredTasks,
    hiddenCompletedHeaderIds,
    isOfflineUsersOpen,
    selectedHeaderId,
    taskFontSize,
    collapsedHeaderIds,
    visibleCalendars,
    enabledCalendars,
    googleCalendars,
    googleEvents,
    activeId,
    activeType,
    reactionFilter,
    isUpdateAvailable,
    messages,
    messageLimit,
    appId,
    isCreatingChannel,
    newChannelName,
    newChannelColor,
    createError,
  } = state;

  const cities = props.profile?.cities || [];

  const [isTasksOpen, setIsTasksOpen] = useState(() => {
    const saved = localStorage.getItem('isTasksOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isNotesOpen, setIsNotesOpen] = useState(() => {
    const saved = localStorage.getItem('isNotesOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isChatOpen, setIsChatOpen] = useState(() => {
    const saved = localStorage.getItem('isChatOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isSidebarCompact, setIsSidebarCompact] = useState(() => {
    const saved = localStorage.getItem('isSidebarCompact');
    return saved !== null ? JSON.parse(saved) : false;
  });

  const [showScaleSlider, setShowScaleSlider] = useState(false);
  const scaleRef = React.useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (scaleRef.current && !scaleRef.current.contains(event.target)) {
        setShowScaleSlider(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    localStorage.setItem('isTasksOpen', JSON.stringify(isTasksOpen));
    localStorage.setItem('isNotesOpen', JSON.stringify(isNotesOpen));
    localStorage.setItem('isChatOpen', JSON.stringify(isChatOpen));
    localStorage.setItem('isSidebarCompact', JSON.stringify(isSidebarCompact));
  }, [isTasksOpen, isNotesOpen, isChatOpen, isSidebarCompact]);

  const personalChannelId = `personal-${user?.uid || 'test-uid-123'}`;
  const personalNoteId = `personal-note-${user?.uid || 'test-uid-123'}`;

  const commonTaskProps = useMemo(() => ({
    currentView,
    username,
    hiddenCompletedHeaderIds,
    activeColorHex,
    dispatch,
    personalTasks: props.personalTasks,
    tasks: props.tasks,
    allTasks: props.allTasks,
    showCompletedTasks: props.showCompletedTasks,
    editingTaskId,
    taskEditRef: props.taskEditRef,
    editTaskText,
    handleSaveEditTask: props.handleSaveEditTask,
    editTaskDueDate,
    editTaskVenueName,
    editTaskAddress,
    editTaskAddressLat,
    editTaskAddressLng,
    handleContextMenu: props.handleContextMenu,
    linkify: props.linkify,
    deletingTaskId,
    deleteTask: props.deleteTask,
    moveToRoot: props.moveToRoot,
    toggleTask: props.toggleTask,
    toggleTaskStar: props.toggleTaskStar,
    handleMoveTaskPriority: props.handleMoveTaskPriority,
    activeId,
    allUsers: props.allUsers,
    participants: activeChannel?.participants || [activeChannel?.createdBy],
    newTask: state.newTask,
    newTaskDate: state.newTaskDate,
    newTaskDueDate: state.newTaskDueDate,
    newTaskIsHeader: state.newTaskIsHeader,
    taskInputRef: props.taskInputRef,
    handleAddTask: currentView === 'home' ? props.handleAddPersonalTask : props.handleAddTask,
    handleAddPersonalTask: props.handleAddPersonalTask,
    channels: props.channels,
    getRootProject: props.getRootProject,
    getProjectPath: props.getProjectPath,
    activeChannelId: currentView === 'home' ? personalChannelId : activeChannelId,
    user,
    selectedHeaderId,
    taskFontSize,
    collapsedHeaderIds,
    showOnlyStarredTasks,
    activeItem: state.activeId
      ? { id: state.activeId, type: state.activeType, text: state.activeId }
      : null,
    isTouch: props.isTouch,
  }), [
    currentView,
    username,
    hiddenCompletedHeaderIds,
    activeColorHex,
    dispatch,
    props.personalTasks,
    props.tasks,
    props.allTasks,
    props.showCompletedTasks,
    editingTaskId,
    props.taskEditRef,
    editTaskText,
    props.handleSaveEditTask,
    editTaskDueDate,
    editTaskVenueName,
    editTaskAddress,
    editTaskAddressLat,
    editTaskAddressLng,
    props.handleContextMenu,
    props.linkify,
    deletingTaskId,
    props.deleteTask,
    props.moveToRoot,
    props.toggleTask,
    props.toggleTaskStar,
    props.handleMoveTaskPriority,
    activeId,
    props.allUsers,
    activeChannel?.participants,
    activeChannel?.createdBy,
    state.newTask,
    state.newTaskDate,
    state.newTaskDueDate,
    state.newTaskIsHeader,
    props.taskInputRef,
    props.handleAddPersonalTask,
    props.handleAddTask,
    props.channels,
    props.getRootProject,
    props.getProjectPath,
    personalChannelId,
    activeChannelId,
    user,
    selectedHeaderId,
    taskFontSize,
    collapsedHeaderIds,
    showOnlyStarredTasks,
    state.activeId,
    state.activeType,
    props.isTouch,
  ]);

  return (
    <div className="flex h-screen w-screen bg-zinc-50 overflow-hidden font-sans text-zinc-900 selection:bg-blue-100 selection:text-blue-900 print:h-auto print:overflow-visible">
      <Sidebar
        {...props}
        isCompact={isSidebarCompact}
        isChatOpen={isChatOpen}
        toggleChat={() => setIsChatOpen(!isChatOpen)}
        isCreatingChannel={isCreatingChannel}
        newChannelName={newChannelName}
        newChannelColor={newChannelColor}
        createError={createError}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-white print:h-auto print:overflow-visible">
        <header className="h-14 px-6 border-b border-zinc-200 flex items-center justify-between bg-white flex-shrink-0 z-40 print:hidden">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-shrink-0">
              <button 
                onClick={() => setIsSidebarCompact(!isSidebarCompact)}
                className="text-zinc-400 hover:text-zinc-600 transition-colors mr-2 p-1 hover:bg-zinc-100 rounded"
                title={isSidebarCompact ? "Expand Sidebar" : "Collapse Sidebar"}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {isSidebarCompact ? (
                    <>
                      <rect width="18" height="18" x="3" y="3" rx="2" />
                      <path d="M9 3v18" />
                      <path d="m14 9 3 3-3 3" />
                    </>
                  ) : (
                    <>
                      <rect width="18" height="18" x="3" y="3" rx="2" />
                      <path d="M9 3v18" />
                      <path d="m16 15-3-3 3-3" />
                    </>
                  )}
                </svg>
              </button>
              <span className="text-zinc-900 tracking-tighter text-lg">
                <span className="font-black">{username}</span>
              </span>
            </div>

            <div className="w-px h-4 bg-zinc-200 mx-1 flex-shrink-0" />

            {currentView === 'workspace' && activeChannel ? (
              <Breadcrumbs
                path={props.getProjectPath(activeChannel.id, props.channels)}
                onNavigate={props.navigate}
                fallbackName={
                  activeChannel.id === 'general_chat_global' ? 'Tiki Bar' : activeChannel.name
                }
              />
            ) : !['calendar', 'database'].includes(currentView) && (
              <span className="text-zinc-400 italic text-sm font-medium tracking-tight truncate max-w-[200px] flex-shrink-0">
                {motivationalMessage || 'Set a motivational message in settings'}
              </span>
            )}

            {['calendar', 'database'].includes(currentView) && (
              <GlobalFilterBar 
                visibleCalendars={visibleCalendars}
                dispatch={dispatch}
                advancedFilters={state.advancedFilters}
                isFilterPanelOpen={state.isFilterPanelOpen}
                isMapPanelOpen={state.isMapPanelOpen}
                googleCalendars={state.googleCalendars}
                profile={props.profile}
                allUsers={props.allUsers}
                saveUserProfile={props.saveUserProfile}
                mapStartDate={state.mapStartDate}
                mapEndDate={state.mapEndDate}
                searchRadius={state.searchRadius}
                customCenter={state.customCenter}
                friendFilter={state.friendFilter}
                selectedTripId={state.selectedTripId}
                trips={(props.events || []).filter(e => e.type === 'trip')}
                calendarItems={props.calendarItems}
                authUser={user}
              />
            )}

            {currentView === 'workspace' && activeChannel && (
              <div className="flex items-center gap-1.5 px-3 py-1 bg-zinc-50 rounded-xl border border-zinc-100 animate-in fade-in slide-in-from-left-2 duration-300 ml-auto">
                <div className="flex -space-x-2">
                  {(activeChannel.participants || [activeChannel.createdBy])
                    .filter((pid) => pid !== user?.uid)
                    .map((pid) => {
                      const p = (props.allUsers || []).find((u) => u.id === pid);
                      if (!p) return null;
                      return (
                        <div
                          key={pid}
                          className="ring-2 ring-white rounded-lg overflow-hidden transition-transform hover:scale-110 hover:z-10 cursor-help"
                          title={p.name}
                        >
                          <Avatar src={p.avatar} size={20} />
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1 bg-zinc-100/50 p-1 rounded-xl border border-zinc-200/50">
            {currentView !== 'calendar' &&
              currentView !== 'weather' &&
              currentView !== 'watch' &&
              currentView !== 'rss' && (
                <>
                  {activeChannel?.type !== 'dm' && (
                    <>
                      <TabButton
                        data-testid="tasks-btn"
                        active={isTasksOpen}
                        onClick={() => setIsTasksOpen(!isTasksOpen)}
                        icon={<CheckSquareIcon />}
                        label="Tasks"
                      />
                      <TabButton
                        data-testid="notes-btn"
                        active={isNotesOpen}
                        onClick={() => setIsNotesOpen(!isNotesOpen)}
                        icon={<FileTextIcon />}
                        label="Notes"
                      />
                    </>
                  )}

                  <div className="relative" ref={scaleRef}>
                    <TabButton
                      data-testid="tiki-bar-btn"
                      active={isChatOpen}
                      onClick={() => setIsChatOpen(!isChatOpen)}
                      onContextMenu={(e) => {
                        if (currentView === 'home') {
                          e.preventDefault();
                          setShowScaleSlider(!showScaleSlider);
                        }
                      }}
                      icon={currentView === 'home' ? <LayoutDashboardIcon /> : <MessageSquareIcon />}
                      label={currentView === 'home' ? 'Dashboard' : 'Chat'}
                      title={currentView === 'home' ? 'Right click to scale dashboard' : ''}
                    />
                    
                    {showScaleSlider && currentView === 'home' && (
                      <div className="absolute top-full right-0 mt-2 bg-zinc-900 rounded-lg p-3 shadow-xl z-[100] w-48 border border-zinc-700 animate-in fade-in slide-in-from-top-2">
                        <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-2 text-left">Dashboard Scale</div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-zinc-500 font-bold">50%</span>
                          <input 
                            type="range" 
                            min="0.5" 
                            max="1.5" 
                            step="0.05"
                            value={state.dashboardScale || 1.0}
                            onChange={(e) => dispatch({ type: 'SET_DASHBOARD_SCALE', payload: parseFloat(e.target.value) })}
                            className="w-full accent-blue-500 cursor-pointer"
                          />
                          <span className="text-[10px] text-zinc-500 font-bold">150%</span>
                        </div>
                        <div className="text-center mt-1 text-[10px] text-zinc-300 font-bold">
                          {Math.round((state.dashboardScale || 1.0) * 100)}%
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            <div className="flex items-center gap-1">
              {(headerClocks || []).map((clock, idx) => (
                <Clock
                  key={idx}
                  timezone={clock.timezone}
                  city={clock.city}
                  lat={clock.lat}
                  lng={clock.lng}
                />
              ))}
            </div>
          </div>
        </header>

        <main className="flex-1 flex overflow-hidden relative bg-white print:h-auto print:overflow-visible">
          {props.customView ? (
            props.customView
          ) : currentView === 'settings' ? (
            <div className="flex-1 overflow-y-auto bg-white p-10 pb-20">
              <div className="max-w-4xl mx-auto">
                <header className="mb-10 flex items-center justify-between border-b border-zinc-100 pb-6">
                  <div className="flex flex-col">
                    <h1
                      data-testid="settings-title"
                      className="text-3xl font-black text-zinc-900 tracking-tighter uppercase"
                    >
                      Settings
                    </h1>
                    <p className="text-sm text-zinc-400 font-medium">
                      Manage your profile and workspace preferences
                    </p>
                  </div>
                </header>
                <ProfileSettings
                  {...props}
                  initialName={username}
                  initialBio={bio}
                  initialMotivation={motivationalMessage}
                  initialAvatar={userAvatar}
                  initialCities={cities}
                  saveUserProfile={props.saveUserProfile}
                  handleSignOut={props.handleSignOut}
                  user={user}
                  deletedChannels={props.deletedChannels}
                  handleRestoreChannel={props.handleRestoreChannel}
                  handlePurgeChannel={props.handlePurgeChannel}
                  handleImportProject={props.handleImportProject}
                  isImporting={props.isImporting}
                  profile={props.profile}
                  googleCalendars={props.state?.googleCalendars || []}
                />
              </div>
            </div>
          ) : currentView === 'calendar' ? (
            <div className="flex-1 h-full bg-white overflow-hidden">
              <CalendarView
                isDatabasePanelOpen={state.isDatabasePanelOpen}
                tasks={props.tasks}
                personalTasks={props.personalTasks}
                currentView={currentView}
                dispatch={dispatch}
                toggleTask={props.toggleTask}
                handleRemoveFlight={props.handleRemoveFlight}
                googleEvents={props.googleEvents}
                googleCalendars={props.state?.googleCalendars || []}
                visibleCalendars={visibleCalendars}
                navigate={props.navigate}
                calendarItems={props.calendarItems}
                getItemColor={props.getItemColor}
                calendarFontSize={props.calendarFontSize}
                calendarCellHeight={props.calendarCellHeight}
                collapsedHeaderIds={props.collapsedHeaderIds}
                activeColorHex={activeColorHex === '#ffffff' ? '#7c3aed' : activeColorHex}
                profile={props.profile}
                advancedFilters={state.advancedFilters}
                isFilterPanelOpen={state.isFilterPanelOpen}
                isMapPanelOpen={state.isMapPanelOpen}
                habits={props.habits}
                habitLogs={props.habitLogs}
                handleLogHabit={props.handleLogHabit}
                authUser={user}
                allUsers={props.allUsers}
                saveUserProfile={props.saveUserProfile}
                mapStartDate={state.mapStartDate}
                mapEndDate={state.mapEndDate}
                searchRadius={state.searchRadius}
                customCenter={state.customCenter}
                friendFilter={state.friendFilter}
                selectedTripId={state.selectedTripId}
                trips={(props.events || []).filter(e => e.type === 'trip')}
                allTasks={props.allTasks}

              />
            </div>
          ) : currentView === 'weather' ? (
            <div className="flex-1 flex flex-col items-center justify-center bg-white p-10">
              <h1 className="text-3xl font-black text-zinc-900 tracking-tighter uppercase mb-4">
                Coming Soon
              </h1>
              <p className="text-zinc-500 font-medium text-center">
                The weather dashboard is currently undergoing maintenance and will return soon.
              </p>
            </div>
          ) : currentView === 'watch' ? (
            <WatchListenView {...props} />
          ) : currentView === 'rss' ? (
            <RSSView {...props} />
          ) : currentView === 'film-tv' ? (
            <FilmTVTracker {...props} />
          ) : currentView === 'media' ? (
            <MediaView {...props} />
          ) : (
            <>
              <div
                data-testid="tasks-section"
                className={`${
                  isTasksOpen && (currentView === 'home' || activeChannel?.type !== 'dm')
                    ? 'w-[350px] border-r border-zinc-200 opacity-100'
                    : 'w-0 border-r-0 opacity-0 overflow-hidden'
                } flex-shrink-0 h-full flex flex-col bg-white print:hidden transition-all duration-300 ease-in-out`}
              >
                <div className="flex-1 overflow-hidden">
                  <TasksView
                    key={currentView === 'home' ? personalChannelId : activeChannelId}
                    {...commonTaskProps}
                    activeColorHex={currentView === 'home' ? '#7c3aed' : activeColorHex}
                  />
                </div>
              </div>

              <div
                data-testid="notes-section"
                className={`${
                  isNotesOpen && (currentView === 'home' || activeChannel?.type !== 'dm')
                    ? 'flex-1 border-r border-zinc-200 opacity-100 min-w-[300px]'
                    : 'flex-none w-0 border-r-0 opacity-0 overflow-hidden'
                } h-full bg-zinc-50 transition-all duration-300 ease-in-out flex flex-col print:border-none`}
              >
                <div className="flex-[3] min-h-0 w-full">
                  <NotepadView
                    key={currentView === 'home' ? personalNoteId : activeChannelId}
                    user={user}
                    username={username}
                    currentView={currentView}
                    b2Token={props.b2Token}
                    activeChannelId={currentView === 'home' ? personalNoteId : activeChannelId}
                    activeColorHex={currentView === 'home' ? '#7c3aed' : activeColorHex}
                    isSavingNote={isSavingNote}
                    isSpellCheckEnabled={isSpellCheckEnabled}
                    dispatch={dispatch}
                    noteTimeoutRef={props.noteTimeoutRef}
                    isLocalEditRef={props.isLocalEditRef}
                    handleSaveNote={props.handleSaveNote}
                    notepadRef={props.notepadRef}
                    isMediaPlayerOpen={props.state.isMediaPlayerOpen}
                    isMediaPlayerCompact={props.state.isMediaPlayerCompact}
                    activeUploads={props.state.activeUploads}
                    handleBackgroundUpload={props.handleBackgroundUpload}
                  />
                </div>
                {(currentView === 'workspace' || currentView === 'home') && props.state.isMediaPlayerOpen && (
                  <div className="flex-1 min-h-0 print:hidden overflow-hidden flex flex-col border-t border-zinc-200 w-full">
                    {currentView === 'home' ? (
                      <MediaPlayer
                        channelId={null}
                        isGlobal={true}
                        authUser={user}
                        allUsers={props.allUsers}
                        channels={props.channels}
                        activeUploads={props.state.activeUploads}
                        handleBackgroundUpload={props.handleBackgroundUpload}
                      />
                    ) : (
                      <MediaPlayer
                        channelId={activeChannelId}
                        isGlobal={false}
                        authUser={user}
                        allUsers={props.allUsers}
                        activeUploads={props.state.activeUploads}
                        handleBackgroundUpload={props.handleBackgroundUpload}
                      />
                    )}
                  </div>
                )}
              </div>

              <div
                data-testid="chat-section"
                className={`${
                  isChatOpen
                    ? 'w-[400px] opacity-100'
                    : 'w-0 opacity-0 overflow-hidden'
                } flex-shrink-0 h-full flex flex-col bg-white print:hidden transition-all duration-300 ease-in-out`}
              >
                <div className="flex-1 w-[400px] h-full overflow-hidden relative">
                  {currentView === 'home' ? (
                    <div 
                      className="absolute inset-0"
                      style={{ 
                        transform: `scale(${state.dashboardScale || 1.0})`, 
                        transformOrigin: 'top left', 
                        width: `${100 / (state.dashboardScale || 1.0)}%`, 
                        height: `${100 / (state.dashboardScale || 1.0)}%` 
                      }}
                    >
                      <DesktopDashboardWrapper {...props} user={user} />
                    </div>
                  ) : (
                    <ChatView
                      key={activeChannelId}
                      b2Token={props.b2Token}
                      user={user}
                      channels={props.channels}
                      allUsers={props.allUsers}
                      messages={messages}
                      editingMessageId={state.editingMessageId}
                      isChatOpen={true}
                      isDM={isDM}
                      reactionFilter={state.reactionFilter}
                      chatInputHtml={chatInputHtml}
                      handleSendMessage={props.handleSendMessage}
                      handleSaveEditMessage={props.handleSaveEditMessage}
                      handleToggleReaction={props.handleToggleReaction}
                      handleDeleteMessage={props.handleDeleteMessage}
                      handleTogglePinMessage={props.handleTogglePinMessage}
                      handleScrollToMessage={props.handleScrollToMessage}
                      handleStartDM={props.handleStartDM}
                      getDmUser={props.getDmUser}
                      dispatch={props.dispatch}
                      appId={appId}
                      username={username}
                      userAvatar={userAvatar}
                      handleLoadMore={props.handleLoadMore}
                      messageLimit={messageLimit}
                      handleShowProfile={props.handleShowProfile}
                      isMobile={props.isMobile}
                      isTouch={props.isTouch}
                      activeChannelId={activeChannelId}
                      isFullWidth={true}
                      activeColorHex={activeColorHex === '#ffffff' ? '#7c3aed' : activeColorHex}
                    />
                  )}
                </div>
              </div>

            </>
          )}
        </main>
      </div>



      <div className="fixed bottom-0 right-0 p-4 flex flex-row justify-end items-end gap-4 z-50 pointer-events-none">
        {(state.openDMChannelIds || []).map((channelId) => (
          <div key={channelId} className="pointer-events-auto">
            <FloatingDM
              {...props}
              openDMChannelId={channelId}
              b2Token={props.b2Token}
              dmMessages={state.dmMessagesMap?.[channelId] || []}
              user={user}
              profile={props.profile}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default React.memo(DesktopLayout);
